import { getNotificationsEnabled, postTrade, putTrade } from '@moal/api'
import PropTypes from 'prop-types'
import { useContext, useEffect, useMemo, useState } from 'react'
import { EDIT_MESSAGE } from '../../../constants/actions/messageListActions'
import * as FEATURES from '../../../constants/features'
import * as SETTINGS from '../../../constants/settings'
import { AuthenticationContext } from '../../../context/authenticationContext'
import { SiteConfigContext } from '../../../context/siteConfigContext'
import { PRODUCT_CODE } from '../../../utils/ENV'
import { toastSuccess } from '../../../utils/toast'
import ReactQuillEditor from '../../Utilities/ReactQuillEditor'
import Tooltip from '../../Utilities/Tooltip'
import SendNotifications from '../common/SendNotifications'
import ToggleSwitchBasic from '../common/ToggleSwitchBasic'
import UploadImages from '../common/UploadImages'
import PostTradeFormPreview from '../PostTradeFormPreview'

const TRADE_FIELDS_MAX_LENGTH = 70

const defaultValues = {
  type: 'open',
  headline: '',
  body: '',
  option: '',
  images: [],
  itemNumber: PRODUCT_CODE,
  foundBySAM: false,
  sendNotifications: true
}

const PostTradeForm = ({ edit, closeModal, prefill, dispatch }) => {
  const { user } = useContext(AuthenticationContext)

  const { [SETTINGS.FEATURES.key]: enabledFeatures } =
    useContext(SiteConfigContext)
  const foundBySAMenabled = useMemo(
    () => enabledFeatures && enabledFeatures[FEATURES.FOUNDBYSAM],
    [enabledFeatures]
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState({
    state: false,
    message: ''
  })

  const [trade, setTrade] = useState(defaultValues)
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)

  const [keyModifer, setKeyModifier] = useState(0)

  useEffect(() => {
    if (prefill) {
      fetchNotificationsEnabled()
    } else {
      let tempTrade = localStorage.getItem('temp_trade')
      if (tempTrade) {
        tempTrade = JSON.parse(tempTrade)
        setTrade({
          type: tempTrade.type ?? defaultValues.type,
          headline: tempTrade.headline ?? defaultValues.headline,
          body: tempTrade.body ?? defaultValues.body,
          option: tempTrade.option ?? defaultValues.option,
          images: tempTrade.images ?? defaultValues.images,
          itemNumber: PRODUCT_CODE,
          foundBySAM: tempTrade.foundBySAM ?? defaultValues.foundBySAM,
          sendNotifications:
            tempTrade.sendNotifications ?? defaultValues.sendNotifications
        })
        setKeyModifier(keyModifer + 1)
      }
      fetchNotificationsEnabled()
    }
  }, [])

  useEffect(() => {
    if (prefill) return
    localStorage.setItem('temp_trade', JSON.stringify(trade))
  }, [trade])

  useEffect(() => {
    if (prefill) {
      const option = prefill?.extra?.find((obj) => {
        return obj.key === 'option'
      })
      const images = prefill?.extra?.find((obj) => {
        return obj.key === 'images'
      })
      const foundBySAM = prefill?.extra?.find((obj) => {
        return obj.key === 'foundBySAM'
      })

      setTrade({
        _id: prefill._id,
        type: prefill.type,
        headline: prefill.headline,
        body: prefill.body,
        option: option?.value ?? '',
        images: images?.value ?? [],
        itemNumber: PRODUCT_CODE,
        foundBySAM: foundBySAM?.value ?? false,
        sendNotifications: prefill.sendNotifications ?? true
      })
      setKeyModifier(keyModifer + 1)
    }
  }, [prefill])

  const fetchNotificationsEnabled = async () => {
    const response = await getNotificationsEnabled(PRODUCT_CODE)
    if (response.status === 200 && response.data.notificationsEnabled) {
      setNotificationsEnabled(true)
    } else {
      setTrade((old) => ({ ...old, sendNotifications: false }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    toastSuccess(edit ? 'Trade updated.' : 'Trade posted.')
    setError({
      state: false,
      message: ''
    })

    const data = { ...trade, body: sanitizeText(trade.body) }

    if (edit) {
      handlePutTrade({
        user,
        data
      })
      closeModal(false)
    } else {
      handlePostTrade({
        user,
        data
      })
    }

    if (!prefill) handleResetForm()
  }

  const handlePostTrade = async ({ user, data }) => {
    const response = await postTrade({ user, data })
    if (response.status === 201) {
      setLoading(false)
      setError({
        state: false,
        message: ''
      })
    }
  }

  const handlePutTrade = async ({ user, data }) => {
    const response = await putTrade({ user, data })
    if (response && response.status === 200) {
      if (dispatch) {
        dispatch({ type: EDIT_MESSAGE, payload: response.data })
      }
    }
  }

  const returnImages = (imgs) => {
    setTrade((trade) => ({ ...trade, images: imgs }))
  }

  const handleRemoveImages = (e) => {
    e.preventDefault()
    setTrade((trade) => ({ ...trade, images: [] }))
  }

  const handleOnChange = (value) => {
    setTrade((trade) => ({ ...trade, body: value }))
  }

  const sanitizeText = (text) => {
    const emptyParagraphsRegex = /<p><br><\/p>/g
    const nonBreakingSpaceRegex = /&nbsp;/g

    const scrubbedHTML = text
      .replace(emptyParagraphsRegex, '')
      .replace(nonBreakingSpaceRegex, ' ')

    return scrubbedHTML
  }

  const handleResetForm = () => {
    setTrade(defaultValues)
    localStorage.setItem('temp_trade', JSON.stringify(defaultValues))
    setKeyModifier(keyModifer + 1)
  }

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-2 w-full gap-5 pb-5">
      <div className="lg:col-span-1">
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="flex flex-col gap-5">
            <div className="flex items-center">
              <h2 className="mr-3">Post a New Trade</h2>
              <Tooltip>
                Create trade notifications for your users in this section. Use
                the Trade Preview to see a live example of how your notification
                will appear.
              </Tooltip>
            </div>
            <div className="flex flex-row justify-start">
              <div className="flex items-center">
                <label className="mr-2 dark:text-neutral-100">Post Type</label>
                <select
                  value={trade.type}
                  onChange={(e) =>
                    setTrade((trade) => ({ ...trade, type: e.target.value }))
                  }
                  className="shrink rounded border border-neutral-300 p-2 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                >
                  <option value="OPEN">Open</option>
                  <option value="UPDATE">Update</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>
              {foundBySAMenabled && (
                <div className="mb-4 ms-4 flex items-center">
                  <ToggleSwitchBasic
                    value={trade.foundBySAM}
                    label="Found By S.A.M."
                    color="green"
                    onChange={(value) =>
                      setTrade((trade) => ({ ...trade, foundBySAM: value }))
                    }
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <label className="dark:text-neutral-100">
                Headline
                {trade.headline.length >=
                  TRADE_FIELDS_MAX_LENGTH - TRADE_FIELDS_MAX_LENGTH * 0.25 && (
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        trade.headline.length === TRADE_FIELDS_MAX_LENGTH
                          ? 'text-red-500'
                          : ''
                      }
                    >
                      ({trade.headline.length} / {TRADE_FIELDS_MAX_LENGTH})
                    </span>
                  </span>
                )}
              </label>
              <input
                value={trade.headline}
                onChange={(e) =>
                  setTrade((trade) => ({ ...trade, headline: e.target.value }))
                }
                className="input"
                required
                maxLength={TRADE_FIELDS_MAX_LENGTH}
              />
            </div>
            <div className="flex flex-col">
              <label className="dark:text-neutral-100">
                Ticker / Symbol / Option
                {trade.option.length >=
                  TRADE_FIELDS_MAX_LENGTH - TRADE_FIELDS_MAX_LENGTH * 0.25 && (
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        trade.option.length === TRADE_FIELDS_MAX_LENGTH
                          ? 'text-red-500'
                          : ''
                      }
                    >
                      ({trade.option.length} / {TRADE_FIELDS_MAX_LENGTH})
                    </span>
                  </span>
                )}
              </label>
              <input
                value={trade.option}
                onChange={(e) =>
                  setTrade((trade) => ({
                    ...trade,
                    option: e.target.value
                  }))
                }
                className="input"
                required
                maxLength={TRADE_FIELDS_MAX_LENGTH}
              />
            </div>
            <div className="flex flex-col">
              <label className="dark:text-neutral-100">Body</label>
              <ReactQuillEditor
                key={keyModifer}
                initialHtml={trade.body}
                onChange={handleOnChange}
              />
            </div>
            {!loading && (
              <div className="flex flex-col">
                <label className="dark:text-neutral-100">Images</label>
                <UploadImages returnImages={returnImages} />
                {edit && trade.images.length > 0 && (
                  <div className="mt-1">
                    <span className="text-red-500">
                      Adding new images will replace old images.
                    </span>
                    <button
                      className="mt-1 button-cancel"
                      onClick={(e) => handleRemoveImages(e)}
                    >
                      Remove All Images
                    </button>
                  </div>
                )}
              </div>
            )}{' '}
            {/* this isn't the best solution, but hiding the images form when it's submit resets it */}
            {notificationsEnabled && (
              <SendNotifications
                checked={trade.sendNotifications}
                handleChange={(e) => {
                  setTrade((old) => ({
                    ...old,
                    sendNotifications: e.target.checked
                  }))
                }}
              />
            )}
            <div className="flex justify-between">
              {error.state && <p className="text-red-400">{error.message}</p>}
              <input
                type="submit"
                className="button-confirm  w-fit"
                value={loading ? 'Submitting...' : 'Submit'}
              />
              <button className="button-cancel w-fit" onClick={handleResetForm}>
                Reset Form
              </button>
            </div>
          </div>
        </form>
      </div>
      <div className="lg:col-span-1 flex flex-col gap-5">
        <PostTradeFormPreview
          trade={{ ...trade, body: sanitizeText(trade.body) }}
        />
      </div>
    </div>
  )
}

PostTradeForm.propTypes = {
  edit: PropTypes.bool,
  closeModal: PropTypes.func,
  prefill: PropTypes.object,
  dispatch: PropTypes.any
}

export default PostTradeForm
